/**
 * 슬라이드 PDF 내보내기 — 브라우저 사이드 window.print() 패턴.
 * master-of-slide 의 export-pdf.ts 영감, 단순화 버전.
 *
 * 절차:
 *  1. 모든 페이지를 hidden iframe-like print container 에 1920×1080 절대 크기로 렌더
 *  2. @page CSS 로 페이지 크기 1920×1080 landscape, margin 0 강제
 *  3. document.fonts.ready 대기 후 window.print()
 *  4. afterprint 이벤트 또는 setTimeout 후 컨테이너 제거
 *
 * 사용자가 인쇄 다이얼로그에서 "PDF 로 저장" 선택해야 PDF 산출.
 */

const PRINT_STYLE_ID = 'innermate-slide-print-style';

export interface PrintSlidesOptions {
  /** 인쇄용 root element. 외부에서 만든 hidden 컨테이너를 넘겨주면 그것을 print 영역으로 표시. */
  printRoot: HTMLElement;
}

export async function printSlides({ printRoot }: PrintSlidesOptions): Promise<void> {
  const styleId = PRINT_STYLE_ID;
  let styleEl = document.getElementById(styleId) as HTMLStyleElement | null;
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = styleId;
    document.head.appendChild(styleEl);
  }
  printRoot.setAttribute('data-innermate-print-root', '');
  styleEl.textContent = `
    @page { size: 1920px 1080px landscape; margin: 0; }
    @media print {
      html, body { margin: 0 !important; padding: 0 !important; background: #fff !important; }
      body > *:not([data-innermate-print-root]) { display: none !important; }
      [data-innermate-print-root] {
        position: absolute !important;
        inset: 0 !important;
        width: 1920px !important;
        height: auto !important;
        background: #fff !important;
      }
      [data-innermate-print-root] [data-innermate-print-page] {
        width: 1920px !important;
        height: 1080px !important;
        page-break-after: always;
        position: relative;
        overflow: hidden;
        break-inside: avoid;
      }
      [data-innermate-print-root] [data-innermate-print-page]:last-child {
        page-break-after: auto;
      }
    }
  `;

  // 폰트 로드 + paint 안정화 대기.
  if ('fonts' in document) {
    try {
      await (document as Document & { fonts: { ready: Promise<unknown> } }).fonts.ready;
    } catch {
      // ignore
    }
  }
  await new Promise((r) => requestAnimationFrame(() => r(undefined)));

  return new Promise<void>((resolve) => {
    const handleAfter = () => {
      window.removeEventListener('afterprint', handleAfter);
      resolve();
    };
    window.addEventListener('afterprint', handleAfter);
    window.print();
    // afterprint 이벤트가 안 오는 환경(Safari 일부)을 대비한 timeout fallback.
    setTimeout(handleAfter, 1000);
  });
}
